import { memo } from 'react'
import BigNumber from 'bignumber.js'
import styled from 'styled-components'
import { getBalanceNumber } from 'utils/formatBalance'
import { useTranslation } from 'contexts/Localization'
import {
  Flex,
  MetamaskIcon,
  Text,
  TooltipText,
  LinkExternal,
  TimerIcon,
  Skeleton,
  useTooltip,
  Button,
  Link,
  HelpIcon,
} from '@pancakeswap/uikit'
import { BASE_BSC_SCAN_URL } from 'config'
import { useCurrentBlock } from 'state/block/hooks'
import { useVaultPoolByKey, useVaultPools } from 'state/pools/hooks'
import { DeserializedPool } from 'state/types'
import { getAddress, getVaultPoolAddress } from 'utils/addressHelpers'
import { registerToken } from 'utils/wallet'
import { getBscScanLink } from 'utils'
import Balance from 'components/Balance'
import { getPoolBlockInfo } from 'views/Pools/helpers'
import { BIG_ZERO } from 'utils/bigNumber'
import MaxStakeRow from '../../MaxStakeRow'

interface ExpandedFooterProps {
  pool: DeserializedPool
  account: string
}

const ExpandedWrapper = styled(Flex)`
  svg {
    height: 14px;
    width: 14px;
  }
`

const ExpandedFooter: React.FC<ExpandedFooterProps> = ({ pool, account }) => {
  const { t } = useTranslation()
  const currentBlock = useCurrentBlock()

  const {
    stakingToken,
    earningToken,
    totalStaked,
    startBlock,
    endBlock,
    stakingLimit,
    stakingLimitEndBlock,
    contractAddress,
    sousId,
    vaultKey,
    profileRequirement,
  } = pool

  const {
    totalCakeInVault,
    fees: { performanceFee },
  } = useVaultPoolByKey(vaultKey)

  const vaultPools = useVaultPools()
  const cakeInVaults = Object.values(vaultPools).reduce((total, vault) => {
    return total.plus(vault.totalCakeInVault)
  }, BIG_ZERO)

  const tokenAddress = earningToken.address || ''
  const poolContractAddress = getAddress(contractAddress)
  const cakeVaultContractAddress = getVaultPoolAddress(vaultKey)
  const isMetaMaskInScope = !!window.ethereum?.isMetaMask
  const isManualCakePool = sousId === 0

  const { shouldShowBlockCountdown, blocksUntilStart, blocksRemaining, hasPoolStarted, blocksToDisplay } =
    getPoolBlockInfo(pool, currentBlock)

  const endDateStr = pool.isWithTimes
    ? new Date(pool.endTime * 1000).toUTCString().slice(0, 16).replace(',', '')
    : new Date(Date.now() + blocksRemaining * 0.75 * 1000).toUTCString().slice(0, 16).replace(',', '')
  const startDateStr = pool.isWithTimes
    ? new Date(pool.startTime * 1000).toUTCString().slice(0, 16).replace(',', '')
    : new Date(Date.now() + (blocksRemaining + startBlock - endBlock) * 0.75 * 1000)
        .toUTCString()
        .slice(0, 16)
        .replace(',', '')

  const { targetRef, tooltip, tooltipVisible } = useTooltip(
    t('Subtracted automatically from each yield harvest and burned.'),
    { placement: 'bottom-start' },
  )

  const getTotalStakedBalance = () => {
    if (vaultKey) {
      return getBalanceNumber(totalCakeInVault, stakingToken.decimals)
    }
    if (isManualCakePool) {
      const manualCakeTotalMinusAutoVault = new BigNumber(totalStaked).minus(cakeInVaults)
      return getBalanceNumber(manualCakeTotalMinusAutoVault, stakingToken.decimals)
    }
    return getBalanceNumber(totalStaked, stakingToken.decimals)
  }

  const {
    targetRef: totalStakedTargetRef,
    tooltip: totalStakedTooltip,
    tooltipVisible: totalStakedTooltipVisible,
  } = useTooltip(
    t('Total amount of %symbol% staked in this pool', {
      symbol:
        stakingToken.symbol === 'HH'
          ? 'HODL Hand® NFTs'
          : stakingToken.symbol === 'GF'
          ? 'Gem Fighter NFTs'
          : stakingToken.name,
    }),
    {
      placement: 'bottom',
    },
  )

  const getTokenLink = () => {
    switch (stakingToken.symbol) {
      case 'HH':
        return 'https://hodltoken.net/hodl-hands-nfts'
      case 'GF':
        return 'https://gemfighter.game/nfts'
      default:
        return 'test'
    }
  }

  const tooltipContent = pool.isWithTimes ? (
    <div>
      <p>Pool ends on {new Date(pool.endTime * 1000).toUTCString()}</p>
    </div>
  ) : (
    <></>
  )

  const {
    targetRef: targetRefEndTime,
    tooltip: tooltipEndTime,
    tooltipVisible: tooltipVisibleEndTime,
  } = useTooltip(tooltipContent, {
    placement: 'bottom-start',
  })

  return (
    <ExpandedWrapper flexDirection="column">
      {profileRequirement && (profileRequirement.required || profileRequirement.thresholdPoints.gt(0)) && (
        <Flex mb="8px" justifyContent="space-between">
          <Text small>{t('Requirement')}:</Text>
          <Text small textAlign="right">
            {profileRequirement.required && t('Pancake Profile')}{' '}
            {profileRequirement.thresholdPoints.gt(0) && (
              <Text small>
                {profileRequirement.thresholdPoints.toNumber().toLocaleString()} {t('Profile Points')}
              </Text>
            )}
          </Text>
        </Flex>
      )}
      <Flex mb="2px" justifyContent="space-between" alignItems="center">
        <Text small>{t('Staked')}:</Text>
        <Flex alignItems="flex-start">
          {totalStaked && totalStaked.gte(0) ? (
            <>
              <Balance
                small
                value={getTotalStakedBalance()}
                decimals={0}
                unit={` ${
                  stakingToken.symbol === 'NFT'
                    ? 'NFTs'
                    : stakingToken.symbol === 'GF'
                    ? 'Gem Fighter NFTs'
                    : stakingToken.name
                }`}
              />
              <span ref={totalStakedTargetRef}>
                <HelpIcon color="textSubtle" width="20px" ml="6px" mt="4px" />
              </span>
            </>
          ) : (
            <Skeleton width="90px" height="21px" />
          )}
          {totalStakedTooltipVisible && totalStakedTooltip}
        </Flex>
      </Flex>
      {stakingLimit && stakingLimit.gt(0) && (
        <MaxStakeRow
          small
          currentBlock={currentBlock}
          hasPoolStarted={hasPoolStarted}
          stakingLimit={stakingLimit}
          stakingLimitEndBlock={stakingLimitEndBlock}
          stakingToken={stakingToken}
        />
      )}
      {shouldShowBlockCountdown && (
        <Flex mb="2px" justifyContent="space-between" alignItems="center">
          {/* <Text small>{hasPoolStarted ? t('Ends in') : t('Starts in')}:</Text> */}
          <Text small>{hasPoolStarted ? t('Ends') : t('Starts')}:</Text>

          {blocksRemaining || blocksUntilStart ? (
            <Flex alignItems="center">
              {!pool.isWithTimes ? (
                <Link external href={getBscScanLink(hasPoolStarted ? endBlock : startBlock, 'countdown')}>
                  {/* <Balance small value={blocksToDisplay} decimals={0} color="primary" />
                <Text small ml="4px" color="primary" textTransform="lowercase">
                  {t('Blocks')}
                </Text> */}
                  <Text small ml="4px" color="primary">
                    {hasPoolStarted ? endDateStr : startDateStr}
                  </Text>
                  <TimerIcon ml="4px" color="primary" />
                </Link>
              ) : (
                <>
                  <Text small ml="4px" color="primary" ref={targetRefEndTime}>
                    {hasPoolStarted ? endDateStr : startDateStr}
                  </Text>
                  {tooltipVisibleEndTime && tooltipEndTime}
                  <TimerIcon ml="4px" color="primary" />
                </>
              )}
            </Flex>
          ) : (
            <Skeleton width="54px" height="21px" />
          )}
        </Flex>
      )}
      {vaultKey && (
        <Flex mb="2px" justifyContent="space-between" alignItems="center">
          {tooltipVisible && tooltip}
          <TooltipText ref={targetRef} small>
            {t('Performance Fee')}
          </TooltipText>
          <Flex alignItems="center">
            {performanceFee ? (
              <Text ml="4px" small>
                {performanceFee / 100}%
              </Text>
            ) : (
              <Skeleton width="90px" height="21px" />
            )}
          </Flex>
        </Flex>
      )}
      {/* <Flex mb="2px" justifyContent="flex-end">
        <LinkExternal href={`/info/token/${earningToken.address}`} bold={false} small>
          {t('See Token Info')}
        </LinkExternal>
      </Flex> */}
      <Flex mb="2px" justifyContent="flex-end">
        <LinkExternal href={stakingToken.projectLink} bold={false} small>
          {pool.isNft
            ? pool.stakingToken.symbol === 'HH'
              ? t('About HODL Hands® NFTs')
              : t('About Gem Fighter NFTs')
            : t('View Project Site')}
        </LinkExternal>
      </Flex>
      {pool.isNft && (pool.earningToken.symbol === 'GF' || pool.earningToken.symbol === 'HH') && (
        <Flex mb="2px" justifyContent="flex-end">
          <LinkExternal href={earningToken.projectLink} bold={false} small>
            {pool.earningToken.symbol === 'HH' ? t('About HODL Hands') : t('About Gem Fighter NFTs')}
          </LinkExternal>
        </Flex>
      )}
      {poolContractAddress && (
        <Flex mb="2px" justifyContent="flex-end">
          <LinkExternal
            href={`${BASE_BSC_SCAN_URL}/address/${vaultKey ? cakeVaultContractAddress : poolContractAddress}`}
            bold={false}
            small
          >
            {t('View Contract')}
          </LinkExternal>
        </Flex>
      )}

      <Flex mb="2px" justifyContent="flex-end">
        <LinkExternal href={getTokenLink()} bold={false} small>
          {t('Buy %name%', { name: pool.stakingToken.name })}
        </LinkExternal>
      </Flex>

      {/* {account && isMetaMaskInScope && tokenAddress && (
        <Flex justifyContent="flex-end">
          <Button
            variant="text"
            p="0"
            height="auto"
            onClick={() => registerToken(tokenAddress, earningToken.symbol, earningToken.decimals)}
          >
            <Text color="primary" fontSize="14px">
              {t('Add to Metamask')}
            </Text>
            <MetamaskIcon ml="4px" />
          </Button>
        </Flex>
      )} */}
    </ExpandedWrapper>
  )
}

export default memo(ExpandedFooter)
